import { useRef, useState, useEffect, Fragment } from 'react';
import type { JSX } from 'react';

import * as palette from 'pkg/config/palette';

import { formatTime } from 'pkg/timeline';

import MaterialSymbol from 'components/material-symbols';

import { RangeSlider } from 'components/form/inputs/range_slider';
import { EllipsisIcon } from 'components/loaders/spinner';

import * as css from './styles.css';

interface AudioPlayerProps {
	src: string;
	title?: string;
	showTitle?: boolean;
	fileSize?: string;
}

export function AudioPlayer({
	src,
	title,
	showTitle = true,
	fileSize,
}: AudioPlayerProps): JSX.Element {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isSeeking, setIsSeeking] = useState(false);

	const displayTitle = title || src;

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const handleLoadedMetadata = () => {
			setDuration(audio.duration);
			setIsLoading(false);
		};

		const handleTimeUpdate = () => {
			if (!isSeeking) {
				setCurrentTime(audio.currentTime);
			}
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
		};

		const handleError = () => {
			setIsLoading(false);
		};
		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('timeupdate', handleTimeUpdate);
		audio.addEventListener('ended', handleEnded);
		audio.addEventListener('error', handleError);

		return () => {
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
			audio.removeEventListener('timeupdate', handleTimeUpdate);
			audio.removeEventListener('ended', handleEnded);
			audio.removeEventListener('error', handleError);
		};
	}, [isSeeking]);

	const togglePlay = () => {
		const audio = audioRef.current;
		if (!audio) return;

		if (isPlaying) {
			audio.pause();
			setIsPlaying(false);
		} else {
			audio.play();
			setIsPlaying(true);
		}
	};

	const handleSeek = (value: number) => {
		const audio = audioRef.current;
		if (!audio) return;

		const newTime = (value / 100) * duration;
		audio.currentTime = newTime;
		setCurrentTime(newTime);
	};

	const handleBeforeSeek = () => {
		setIsSeeking(true);
	};

	const handleAfterSeek = () => {
		setIsSeeking(false);
	};

	const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

	return (
		<div className={css.container}>
			<audio ref={audioRef} src={src} preload="metadata" />

			{showTitle && (
				<Fragment>
					<div className={css.header}>
						<button
							className={css.playButton}
							onClick={togglePlay}
							disabled={isLoading}
							aria-label={isPlaying ? 'Pause' : 'Play'}>
							{isLoading ? (
								<EllipsisIcon />
							) : (
								<MaterialSymbol
									variant={isPlaying ? 'pause' : 'play_arrow'}
									scale={1.2}
								/>
							)}
						</button>

						<span className={css.title} title={displayTitle}>
							{displayTitle}
						</span>

						{!isLoading && (
							<div className={css.metadata}>
								<span className={css.duration}>
									{formatTime(duration)}
									{fileSize && ` (${fileSize})`}
								</span>
							</div>
						)}
					</div>
				</Fragment>
			)}

			<div className={css.controls}>
				<div className={css.sliderContainer}>
					<RangeSlider
						min={0}
						max={100}
						step={0.1}
						value={progress}
						thumbColor={palette.white}
						trackColor={palette.gray[300]}
						progressColor={palette.blue[500]}
						withButtons={false}
						thumbSize="small"
						onChange={handleSeek}
						onBeforeChange={handleBeforeSeek}
						onAfterChange={handleAfterSeek}
					/>
				</div>

				<span className={css.time}>{formatTime(currentTime)}</span>
			</div>
		</div>
	);
}
